import { useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { ScrollResetBoundaryContext } from "#/hooks/useScrollResetBoundary";
import { shouldResetScroll } from "#/utils/navigationUtils";

/**
 * Restores the "a new page starts at the top" behaviour that an SPA loses:
 * `pushState` never resets the scroll offset, declarative React Router has no
 * scroll handling of its own, and the document element that actually scrolls is
 * never unmounted between routes — so it keeps the offset of the page the user
 * just left.
 *
 * Route-based tab shells opt out via `useScrollResetBoundary`: switching tabs
 * inside one keeps the current position. Tab strips driven by the `tab` search
 * param do reset, since they swap the whole page body.
 */
export const ScrollReset = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  // Lazily created once and never replaced, so the context value stays stable
  // and registering a boundary never re-renders the app.
  const [boundaries] = useState(() => new Set());
  const previousPathnameRef = useRef(pathname);
  const previousSearchRef = useRef(search);

  // Layout effect rather than a passive one: the reset has to land before
  // paint, otherwise the new page shows up at the old offset for a frame.
  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const previousSearch = previousSearchRef.current;
    previousPathnameRef.current = pathname;
    previousSearchRef.current = search;

    const reset = shouldResetScroll({
      previousPathname,
      pathname,
      boundaries: [...boundaries],
      hash,
      previousSearch,
      search,
    });

    if (reset) {
      window.scrollTo(0, 0);
    }
  }, [pathname, search, hash, boundaries]);

  return (
    <ScrollResetBoundaryContext.Provider value={boundaries}>
      {children}
    </ScrollResetBoundaryContext.Provider>
  );
};
